/**
 * How one orphan row reads, as sentences rather than as blanks.
 *
 * Every column here can legitimately be absent, and each absence means
 * something different. A blank cell says "no value" for all of them, which is
 * the failure this page exists to avoid: it is read during a migration by
 * somebody deciding whether a number moving is a problem.
 */

import type { OrphanSessionEvent } from '../api'

/** What the routing policy would have done with this card.
 *
 *  Absent on a stop-transaction, whose body carries no identifier at all, so
 *  there is nothing for the predicate to read. That is an answer and it is
 *  written as one: a blank would read as a value we failed to record. */
export function orphanRoute(event: OrphanSessionEvent): string {
  if (event.route_label) return event.route_label
  if (event.would_have_been) return event.would_have_been
  return 'no identifier in this event'
}

/** What the fallback forward did.
 *
 *  Null is not a failure. It means this row was written and the outcome never
 *  came back, which happens if the process stopped between the two. Zero is
 *  Service OCPI's own "never connected", and it is a failure. Those must not
 *  render alike. */
export function orphanForward(event: OrphanSessionEvent): string {
  const status = event.forward_status
  if (status === null || status === undefined) return 'not recorded'
  if (status === 0) return 'never connected'
  const latency =
    event.forward_latency_ms === null || event.forward_latency_ms === undefined
      ? ''
      : `, ${event.forward_latency_ms} ms`
  return `${status}${latency}`
}

export interface EstateReading {
  /** The toolkit's own badge tones. `info` for not known rather than a colour
   *  that reads as a verdict: the eye takes colour first, and warning on a
   *  charge point nobody asked about is an accusation. */
  status: 'success' | 'warning' | 'info'
  label: string
}

/** Whether the charge point is in the estate at all.
 *
 *  The real question behind "why is this an orphan". A station excluded in the
 *  OCPP Server never sends an authorization and its transaction events arrive
 *  regardless, so the row exists and nothing upstream of it does.
 *
 *  Three states, not two. Unknown is when this deployment has no estate
 *  resolver, and rendering that as "no" would accuse a charge point of being
 *  outside an estate nobody asked about. */
export function orphanEstate(inEstate: boolean | null | undefined): EstateReading {
  if (inEstate === true) return { status: 'success', label: 'yes' }
  if (inEstate === false) return { status: 'warning', label: 'no' }
  return { status: 'info', label: 'not known' }
}

/**
 * Whether a decision explains this row, in words.
 *
 * Four answers, and the fourth is today's majority. A blank cell would say "no
 * authorization existed" for every row that cannot be looked up, and on
 * production that is the opposite of the truth: the authorization existed and
 * was recorded under a different identifier.
 *
 * `several` shows a count and no verdict on purpose. Two connectors at one
 * charge point are two sessions, and on the same card they are two decisions.
 * Choosing between them is the coin flip DEC-09 refuses for settlement, and a
 * page that guesses teaches its reader to trust a guess.
 */
export function orphanDecision(decision: OrphanSessionEvent['decision']): string {
  if (!decision) return 'not looked up'
  switch (decision.state) {
    case 'found': {
      const verdict = [decision.outcome, decision.authority && `via ${decision.authority}`]
        .filter(Boolean)
        .join(' ')
      // TOO LATE TO BIND IS A THIRD ANSWER, and it was unreachable. The search
      // and the bind used one number, so a decision the bind had missed could
      // never be found, and this row read as "no authorization for this card
      // here": a positive claim that none existed, in the one case where one
      // did. Now the search looks further and this is what it can say.
      return decision.within_bind_window === false
        ? `${verdict}, ${inWords(decision.seconds_before)} before the start, too late to bind${bindWindow(decision)}`
        : verdict
    }
    case 'none':
      // BOUNDED, NEVER ABSOLUTE. This service looked back a stated distance
      // and found nothing there. It did not look at all of history and cannot
      // say that nothing exists.
      return `none within ${inWords(decision.searched_seconds) ?? 'the window searched'}`
    case 'several':
      return [
        `${decision.candidates ?? 0} authorizations, none named`,
        decision.seconds_before != null && `nearest ${inWords(decision.seconds_before)} before the start`,
      ]
        .filter(Boolean)
        .join(', ')
    default:
      return decision.detail ?? 'cannot be looked up'
  }
}

/** A gap in words. Null in, null out: an unreadable time is not zero. */
function inWords(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds)) return null
  const minutes = Math.round(seconds / 60)
  if (minutes < 1) return `${Math.round(seconds)} seconds`
  if (minutes < 120) return `${minutes} minute${minutes === 1 ? '' : 's'}`
  const hours = Math.round(minutes / 60)
  return `${hours} hour${hours === 1 ? '' : 's'}`
}

/** The bind window, named beside the gap, so the reader can see why it missed. */
function bindWindow(decision: NonNullable<OrphanSessionEvent['decision']>): string {
  const bind = inWords(decision.bind_window_seconds)
  return bind ? ` (bind window ${bind})` : ''
}

/**
 * What the Overview's orphan card says under its number.
 *
 * The number is the window and the total is context. Naming both matters: the
 * total only grows, because nothing deletes an orphan row, so a reader who
 * takes it for the current state concludes a repair failed when the rate has
 * already gone to zero.
 */
export function orphanCaption(recently: number, total: number, over = 'the last hour'): string {
  if (recently > 0) {
    return `In ${over}, routed by default because nothing named the session. ${total} in all`
  }
  if (total > 0) {
    return `None in ${over}. ${total} in all, and that number does not fall`
  }
  return 'Every session event named its authority'
}
